import { graphql } from '../../gql';
import { useApiMutation } from '../../query/hooks';
import { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { z } from 'zod';
import { Button, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { Stack } from '@mui/material';
import { ControlledFormField } from '../../form/ControlledFormField';
import { JourneySelect } from '../../journey/JourneySelect';
import { PhaseSelect } from '../../journey/PhaseSelect';
import { ActivitySelect } from '../../journey/ActivitySelect';
import { Divider } from '@mui/material';
import { FormErrors } from '../../form/FormErrors';
import { useTypedForm } from '../../../../shared/fe/hooks/useForm';
import { useModalContext } from '../../modal/ModalContext';
import { mutationResponseHandler } from '../../utils/mutationResponseHandler';
import { useSnackbar } from '../../store/snackbar';

const assignActionMutation = graphql(`
	mutation assignActionModal_updateAction($id: UUID!, $data: ActionUpdateInput!) {
		updateAction(by: { id: $id }, data: $data) {
			ok
			errorMessage
		}
	}
`);

const createFormSchema = () =>
	z.object({
		journeyId: z.string(),
		phaseId: z.string(),
		activityId: z.string(),
	});

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

const actionAssignmentModalSchema = z.object({
	actionId: z.string(),
});

const ActionAssignmentModal: FC<z.infer<typeof actionAssignmentModalSchema>> = ({ actionId }) => {
	const { closeModal } = useModalContext();
	const intl = useIntl();
	const { openSnackbar } = useSnackbar();

	const form = useTypedForm({
		schema: createFormSchema(),
	});

	const { mutateAsync: updateActivityAsync } = useApiMutation(assignActionMutation, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: result.updateAction.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'form.assign-action.success' }),
					error: intl.formatMessage({ id: 'form.assign-action.error' }),
				},
				callback: closeModal,
				openSnackbar,
			});
		},
	});

	const handleSubmit = (values: FormValues) => {
		updateActivityAsync({
			id: actionId,
			data: {
				activities: [
					{
						connect: {
							id: values.activityId,
						},
					},
				],
			},
		});
	};

	const values = form.watch();

	return (
		<>
			<DialogTitle>
				<FormattedMessage id={'form.action-assignment.title'} />
			</DialogTitle>
			<Divider />
			<form noValidate onSubmit={form.handleSubmit(handleSubmit)}>
				<DialogContent>
					<Stack spacing={3}>
						<ControlledFormField
							form={form}
							name={'journeyId'}
							label={intl.formatMessage({ id: 'form.assign-action.label.journey' })}
							description={intl.formatMessage({ id: 'form.assign-action.label.journey.description' })}
							render={(props) => <JourneySelect {...props} />}
						/>

						<ControlledFormField
							form={form}
							name={'phaseId'}
							label={intl.formatMessage({ id: 'form.assign-action.label.phase' })}
							render={(props) => <PhaseSelect journeyId={values.journeyId} {...props} />}
						/>

						<ControlledFormField
							form={form}
							name={'activityId'}
							label={intl.formatMessage({ id: 'form.assign-action.label.activity' })}
							render={(props) => <ActivitySelect phaseId={values.phaseId} {...props} />}
						/>

						<FormErrors form={form} />
					</Stack>
				</DialogContent>
				<DialogActions sx={{ p: 2.5 }}>
					<Stack direction={'row'} spacing={2} alignItems={'center'} width={'100%'} justifyContent={'end'}>
						<Button color={'secondary'} onClick={closeModal}>
							<FormattedMessage id={'common.cancel'} />
						</Button>
						<Button type={'submit'} variant={'contained'} disabled={form.formState.isSubmitting}>
							<FormattedMessage id={'common.save'} />
						</Button>
					</Stack>
				</DialogActions>
			</form>
		</>
	);
};

export { actionAssignmentModalSchema, ActionAssignmentModal };
